import { Box, Slider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { percentageNoDecimals } from "../shared/percentage";
import { TABULAR_NUMERALS } from "./general/dataAtoms/shared";
import { QUIET_SLIDER } from "./general/tableStyles";
import { WeightedEstimate } from "./general/WeightedEstimates";

export type WeightedAwardEditorProps = {
  // what the award is called among the estimates
  label: string;
  // the award as a share of the claimed amount
  value: number;
  // the award as an amount, formatted
  amount: string;
  weight?: number;
  share?: number;
  onValueChange: (newValue: number) => void;
  onWeightChange?: (newValue: number) => void;
  onDelete?: () => void;
};

export const WeightedAwardEditor = ({
  label,
  value,
  amount,
  weight,
  share,
  onValueChange,
  onWeightChange,
  onDelete,
}: WeightedAwardEditorProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "weightedAwards" });

  return (
    <WeightedEstimate
      kind="weightedAwards"
      weight={weight}
      share={share}
      onWeightChange={onWeightChange}
      onDelete={onDelete}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}{" "}
          <Box
            component="strong"
            sx={{ ml: 0.5, fontWeight: 700, ...TABULAR_NUMERALS }}
          >
            {amount}
          </Box>
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}
        >
          {t("label.percentage", {
            percentage: percentageNoDecimals.format(value),
          })}
        </Typography>
      </Box>
      <Slider
        size="small"
        value={value * 100}
        aria-label={label}
        // the share stands over the slider and follows it
        valueLabelDisplay="off"
        onChange={(_, newValue) => {
          if (typeof newValue !== "number") return;
          if (newValue >= 0) onValueChange(newValue / 100);
        }}
        sx={{ display: "block", py: 1.25, ...QUIET_SLIDER }}
      />
    </WeightedEstimate>
  );
};
